import { useCallback, useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { meetingNotepadModeAtom, setMeetingNotepadSizeAtom } from './atoms'
import {
  meetingNotepadDraggingAtom,
  meetingNotepadRestingHeightAtom,
  useMeetingNotepadHeights,
  NOTEPAD_EXPANDED_PX,
} from './notepad-height'

/**
 * The notepad's top edge as a resize target: the height range, where a drag
 * starts from, and the drag's own lifecycle.
 *
 * The floor is the height the notepad has always opened at: the drag is for
 * making more room, and an editor shorter than the one the design settled on
 * isn't a size anyone is reaching for. Getting it out of the way is what
 * collapsing is for, and that stays on the title row.
 *
 * The three sizes stay the discriminant and the drag lands *between* them: it
 * writes the resting height and leaves the size on `normal`, except at the top
 * of the range, where it snaps to `full` so the header's button keeps reading
 * the truth about where the edge ended up.
 *
 * That snap deliberately doesn't touch the resting height. It's what lets the
 * full-height button hand back the height the user dragged out rather than the
 * one the notepad happened to open at.
 */
export function useMeetingNotepadResize() {
  const mode = useAtomValue(meetingNotepadModeAtom)
  const setSize = useSetAtom(setMeetingNotepadSizeAtom)
  const setRestingHeight = useSetAtom(meetingNotepadRestingHeightAtom)
  const setDragging = useSetAtom(meetingNotepadDraggingAtom)
  const isDragging = useAtomValue(meetingNotepadDraggingAtom)
  const heights = useMeetingNotepadHeights()
  const maxHeight = heights.full

  // Where a drag starts from, which is not always what's on screen: a collapsed
  // notepad is below the floor, so grabbing its edge picks the range up at the
  // bottom rather than reporting a position the handle can't be moved to.
  const height = Math.max(heights[mode?.size ?? 'normal'], NOTEPAD_EXPANDED_PX)

  const resize = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, NOTEPAD_EXPANDED_PX), maxHeight)
      if (clamped >= maxHeight) {
        setSize('full')
        return
      }
      setRestingHeight(clamped)
      setSize('normal')
    },
    [maxHeight, setSize, setRestingHeight],
  )

  const startDrag = useCallback(() => setDragging(true), [setDragging])
  const endDrag = useCallback(() => setDragging(false), [setDragging])

  // A notepad closed mid-drag (its toggle is still live behind the pointer)
  // would otherwise leave the footer slot's morph switched off for good.
  useEffect(() => endDrag, [endDrag])

  return { height, minHeight: NOTEPAD_EXPANDED_PX, maxHeight, isDragging, resize, startDrag, endDrag }
}
